import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "./common/Navbar";

const VideoHeroSection: React.FC = () => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  // Handle image load event
  const handleImageLoaded = () => {
    setIsImageLoaded(true);
  };

  return (
    <section className="relative w-full min-h-[100vh] bg-black">
      {/* Background image */}
      <div className="absolute inset-0 w-full h-full">
        {/* Loading placeholder */}
        {!isImageLoaded && (
          <div className="absolute inset-0 bg-gray-900 animate-pulse">
            <span className="sr-only">Loading image...</span>
          </div>
        )}

        {/* Image */}
        <img
          className={`w-full h-full object-cover ${isImageLoaded ? "opacity-100" : "opacity-0"
            } transition-opacity duration-1000`}
          src="/Home/fivesafari.jpg"
          alt="Namibia Safari Background"
          onLoad={handleImageLoaded}
          fetchPriority="high"
        />

        {/* Overlay gradient */}
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(circle, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.7) 100%)'
        }}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col min-h-[100vh]">
        {/* Header navigation */}
        <Navbar />

        {/* Main content with auto height to fill available space */}
        <div className="container mx-auto px-4 flex-grow flex flex-col justify-between py-4">
          {/* Upper content */}
          <div className="text-white text-center mx-auto max-w-3xl mt-2 mb-4">
            <motion.p
              className="bg-gradient-to-r from-[#C05B2A] via-amber-500 to-[#C05B2A] bg-clip-text text-transparent font-poppins tracking-wider text-sm"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              DISCOVER · EXPERIENCE · REMEMBER
            </motion.p>

            <motion.h1
              className="font-aboreto font-bold tracking-wide mt-2 mb-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <span className="text-white text-5xl md:text-6xl block">RION WILDLIFE</span>
              <span className="bg-gradient-to-r from-[#C05B2A] via-amber-500 to-[#C05B2A] bg-clip-text text-transparent text-4xl md:text-5xl block">SAFARIS</span>
            </motion.h1>

            {/* Founder image */}
            <motion.div
              className="relative w-20 h-20 mx-auto my-3"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="absolute inset-0 rounded-full border-2 border-[#C05B2A]">
                <img
                  src="/Home/Mr.Rion Propic.jpeg"
                  alt="Mr. Rion"
                  className="w-full h-full object-cover rounded-full"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://via.placeholder.com/150?text=Mr.+Rion';
                  }}
                />
              </div>
              <div className="absolute -bottom-1 -right-1 bg-[#C05B2A] text-white text-[8px] px-1.5 py-0.5 rounded-full">
                Founder
              </div>
            </motion.div>

            <motion.div
              className="backdrop-blur-sm bg-black/20 p-3 rounded-lg border-l-2 border-[#C05B2A]/40 max-w-2xl mx-auto my-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <p className="text-white/90 text-sm font-poppins">
                Let Namibia's soul guide you through <span className="text-[#C05B2A] font-medium">untamed landscapes</span> and <span className="text-[#C05B2A] font-medium">extraordinary wildlife</span> encounters led by a true local expert.
              </p>
            </motion.div>

            {/* Achievement badges */}
            <motion.div
              className="flex justify-center gap-3 my-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <div className="flex items-center gap-1 bg-black/20 backdrop-blur-sm px-2 py-1 rounded-full border border-white/10">
                <div className="text-[#C05B2A] bg-[#C05B2A]/10 p-1 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-white text-xs font-medium">20+ Years Experience</span>
              </div>
              <div className="flex items-center gap-1 bg-black/20 backdrop-blur-sm px-2 py-1 rounded-full border border-white/10">
                <div className="text-[#C05B2A] bg-[#C05B2A]/10 p-1 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
                <span className="text-white text-xs font-medium">5-Star Rated</span>
              </div>
              <div className="flex items-center gap-1 bg-black/20 backdrop-blur-sm px-2 py-1 rounded-full border border-white/10">
                <div className="text-[#C05B2A] bg-[#C05B2A]/10 p-1 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-white text-xs font-medium">Local Expertise</span>
              </div>
            </motion.div>

            {/* CTA button */}
            <motion.div
              className="mt-2 mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
            >
              <Link
                to="/tours"
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-[#C05B2A] to-[#A64D23] text-white font-bold rounded-md text-sm hover:from-[#A64D23] hover:to-[#8A3E1E]"
              >
                EXPLORE ALL SAFARIS
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </motion.div>
          </div>

          {/* Safari Cards - positioned at bottom */}
          <motion.div
            className="grid grid-cols-3 gap-3 mt-auto mx-auto w-full max-w-5xl mb-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            {/* Card 1 */}
            <Link to="/tours/big-five-safari" className="group relative bg-black/30 rounded-xl overflow-hidden border border-white/10 hover:transform hover:scale-[1.02] transition-all duration-300">
              <img
                src="/tour-images/bigfive.jpeg"
                alt="Big Five Safari"
                className="w-full aspect-[16/9] object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-2">
                <h3 className="text-white text-sm font-bold truncate group-hover:text-[#C05B2A] transition-colors">Big Five Safari</h3>
                <p className="text-white/80 text-xs line-clamp-1 mb-1">
                  Etosha, Chobe, Big Five wildlife spotting.
                </p>
                <div className="flex items-center justify-between">
                  <span className="bg-[#C05B2A] rounded-sm px-1.5 py-0.5 text-[10px] uppercase font-bold">Popular</span>
                  <span className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-[#C05B2A] mr-0.5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span className="text-white text-[10px]">4.9/5</span>
                  </span>
                </div>
              </div>
            </Link>

            {/* Card 2 */}
            <Link to="/tours/namibia-highlights" className="group relative bg-black/30 rounded-xl overflow-hidden border border-white/10 hover:transform hover:scale-[1.02] transition-all duration-300">
              <img
                src="/tour-images/namibia-highlights.jpeg"
                alt="Namibia Highlights Safari"
                className="w-full aspect-[16/9] object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-2">
                <h3 className="text-white text-sm font-bold truncate group-hover:text-[#C05B2A] transition-colors">Namibia Highlights</h3>
                <p className="text-white/80 text-xs line-clamp-1 mb-1">
                  Windhoek, Kalahari, Sossusvlei, Etosha Park.
                </p>
                <div className="flex items-center justify-between">
                  <span className="bg-[#C05B2A] rounded-sm px-1.5 py-0.5 text-[10px] uppercase font-bold">Featured</span>
                  <span className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-[#C05B2A] mr-0.5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span className="text-white text-[10px]">4.8/5</span>
                  </span>
                </div>
              </div>
            </Link>

            {/* Card 3 */}
            <Link to="/tours/namwild-safari" className="group relative bg-black/30 rounded-xl overflow-hidden border border-white/10 hover:transform hover:scale-[1.02] transition-all duration-300">
              <img
                src="/tour-images/namwild.jpeg"
                alt="Namwild Safari"
                className="w-full aspect-[16/9] object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-2">
                <h3 className="text-white text-sm font-bold truncate group-hover:text-[#C05B2A] transition-colors">Namwild Safari</h3>
                <p className="text-white/80 text-xs line-clamp-1 mb-1">
                  Windhoek, Swakopmund, Etosha exploration.
                </p>
                <div className="flex items-center justify-between">
                  <span className="bg-[#C05B2A] rounded-sm px-1.5 py-0.5 text-[10px] uppercase font-bold">Authentic</span>
                  <span className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-[#C05B2A] mr-0.5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span className="text-white text-[10px]">4.9/5</span>
                  </span>
                </div>
              </div>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default VideoHeroSection; 